import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  ExtensionFieldExposureMapSchema,
  ExtensionFieldExposureSchema,
  ExtensionColumnsMetadataSchema,
  InternalUiSpecSchema,
  ExtensionValueSchema,
  EXTENSION_TARGET_MODELS,
} from "@erp/shared-types";

/**
 * Entity extensions — user-defined metadata columns attached to an existing
 * entity, provisioned as a physical table in the app DB's `schema_ext` schema.
 *
 * Provisioning and policy procedures are administrative. Source discovery and
 * generated detail reads use the extended entity's read permission.
 */

const UUIDSchema = z.string().uuid();

const FieldExposureResponseSchema = z.object({
  extensionId: UUIDSchema,
  provisionedVersionId: UUIDSchema,
  fieldExposure: ExtensionFieldExposureMapSchema,
});

const SchemaExtensionSchema = z.object({
  id: UUIDSchema,
  targetModel: z.string(),
  schemaDefinitionId: UUIDSchema,
  /** Table name within the `schema_ext` schema. */
  tableName: z.string(),
  /** The version snapshot the provisioned columns currently reflect. */
  provisionedVersionId: UUIDSchema,
});

const ColumnChangeSchema = z.object({
  kind: z.enum(["add", "rename", "retype", "drop"]),
  fieldId: z.string(),
  /** Present on rename: the current column name. */
  from: z.string().optional(),
  /** Present on rename: the name it becomes. */
  to: z.string().optional(),
  /** Present on retype/drop: the column the change targets. */
  columnName: z.string().optional(),
});

const StatusResponseSchema = z.object({
  provisioned: z.boolean(),
  /** Which entity it extends, when provisioned. */
  targetModel: z.string().nullable(),
  tableName: z.string().nullable(),
  tableExists: z.boolean(),
  provisionedVersionId: UUIDSchema.nullable(),
  activeVersionId: UUIDSchema.nullable(),
  /** What a sync would apply, in the order it would apply it. */
  pendingChanges: z.array(ColumnChangeSchema),
  /** Edits a sync refuses because they would lose data. */
  rejectedChanges: z.array(
    z.object({ fieldName: z.string(), reason: z.string() }),
  ),
  /** Columns the provisioned snapshot expects that the table doesn't have. */
  missingColumns: z.array(z.string()),
  /** Actual physical columns, in database order, mapped to the provisioned snapshot. */
  physicalColumns: z.array(
    z.object({
      name: z.string(),
      /** PostgreSQL's formatted type, including declared length/precision. */
      dbType: z.string(),
      nullable: z.boolean(),
      /** Unmapped columns exist in storage but belong to neither system nor snapshot. */
      kind: z.enum(["system", "configurable", "unmapped"]),
      fieldId: z.string().nullable(),
    }),
  ),
  /**
   * Table-vs-schema drift, rendered by the UI. `syncable` marks the ones
   * `sync` can repair. `type_mismatch` / `nullability_mismatch` (storage that
   * no longer matches the provisioned version) also carry the column, its
   * field ID (null for system columns) and expected/actual.
   */
  issues: z.array(
    z.object({
      severity: z.enum(["error", "warning"]),
      code: z.string(),
      message: z.string(),
      syncable: z.boolean(),
      columnName: z.string().optional(),
      fieldId: z.string().nullable().optional(),
      expected: z.string().optional(),
      actual: z.string().optional(),
    }),
  ),
});

/**
 * The minimum a field needs for socket generation: `socketTypeForField` reads the
 * type and `isMultiple`, and the label names the socket.
 */
const SocketFieldSchema = z.object({
  id: z.string(),
  fieldName: z.string(),
  fieldType: z.string(),
  label: z.string(),
  isRequired: z.boolean(),
  isMultiple: z.boolean(),
  sortOrder: z.number(),
  description: z.string().nullable().optional(),
});

export const schemaExtensionsContracts = {
  columnSources: oc
    .input(z.object({ tableId: z.string().min(1) }))
    .output(z.object({ columns: ExtensionColumnsMetadataSchema }))
    .route({
      method: "GET",
      summary: "Discover exposed extension columns",
      description:
        "Returns provisioned extension fields exposed as columns for a registered table, subject to the entity's read permission.",
    }),
  entityViews: oc
    .input(
      z.object({
        targetModel: z.enum(EXTENSION_TARGET_MODELS),
        entityKey: z.string().min(1),
        surface: z.literal("detail"),
        extensionIds: z.array(UUIDSchema).max(100).optional(),
      }),
    )
    .output(
      z.object({
        surface: z.literal("detail"),
        views: z.array(
          z.object({
            extensionId: UUIDSchema,
            kind: z.literal("generated"),
            spec: InternalUiSpecSchema,
            state: z.object({
              values: z.record(z.string(), ExtensionValueSchema),
            }),
          }),
        ),
        extensionColumns: ExtensionColumnsMetadataSchema,
      }),
    )
    .route({
      method: "GET",
      summary: "Read generated extension detail views",
      description:
        "Returns generated detail layouts and detail-exposed values for one organization-owned entity, with per-field value issues.",
    }),
  getFieldExposure: oc
    .input(z.object({ extensionId: UUIDSchema }))
    .output(FieldExposureResponseSchema)
    .route({
      method: "GET",
      summary: "Get effective extension field exposure",
      description:
        "Returns the immediate exposure policy for provisioned fields of an organization-owned extension; requires schema administration permission.",
    }),

  setFieldExposure: oc
    .input(
      z.object({
        extensionId: UUIDSchema,
        fieldExposure: ExtensionFieldExposureMapSchema,
      }),
    )
    .output(FieldExposureResponseSchema)
    .route({
      method: "POST",
      summary: "Save immediate extension field exposure",
      description:
        "Replaces the exposure policy for an organization-owned extension after validating provisioned field IDs and schema administration permission; takes effect immediately.",
    }),
  updateFieldExposure: oc
    .input(
      z.object({
        extensionId: UUIDSchema,
        fieldId: z.string().min(1),
        exposure: ExtensionFieldExposureSchema.keyof(),
        value: z.boolean(),
      }),
    )
    .output(FieldExposureResponseSchema)
    .route({
      method: "POST",
      summary: "Set one extension field exposure switch",
      description:
        "Sets a single exposure switch for one provisioned field of an organization-owned extension, leaving every other field and switch unchanged; requires schema administration permission and takes effect immediately.",
    }),
  /**
   * Socket sources for every provisioned extension of an entity: the table (which
   * namespaces the socket ids), the schema name (which labels them) and the
   * fields of the version the table currently reflects.
   *
   * The editor draws its `ext:` sockets from this and the workflow save path
   * canonicalizes from the same data, so the canvas and the server can't disagree
   * about what a node's sockets are.
   */
  listSocketSources: oc
    .input(z.object({ targetModel: z.string() }))
    .output(
      z.array(
        z.object({
          targetModel: z.string(),
          schemaDefinitionId: UUIDSchema,
          extensionName: z.string(),
          fields: z.array(SocketFieldSchema),
        }),
      ),
    )
    .route({
      summary: "List extension fields for workflow sockets",
      description:
        "Returns each provisioned extension of an entity with its schema name and the fields of its provisioned version, used to build workflow node sockets.",
      method: "GET",
    }),

  list: oc
    .input(z.object({ targetModel: z.string() }))
    .output(z.array(SchemaExtensionSchema))
    .route({
      summary: "List entity extensions",
      description:
        "Returns the provisioned extensions of one entity type, each with its schema, extension table name, and provisioned version.",
      method: "GET",
    }),

  status: oc
    .input(z.object({ schemaDefinitionId: UUIDSchema }))
    .output(StatusResponseSchema)
    .route({
      summary: "Get entity extension status",
      description:
        "Reports whether a schema's extension is provisioned, its table and versions, pending and rejected column changes, missing columns, storage type/nullability drift, and which issues a sync can fix.",
      method: "GET",
    }),

  /**
   * What publishing the schema's current draft would do to its extension table.
   * Drives the destructive-change confirmation prompt: `destructive` non-empty
   * means `schemaDefinitions.publishVersion` will demand
   * `extensionSyncConfirmation` equal to `extensionName`.
   */
  previewPublishSync: oc
    .input(z.object({ schemaDefinitionId: UUIDSchema }))
    .output(
      z.object({
        registered: z.boolean(),
        extensionName: z.string().nullable(),
        tableName: z.string().nullable(),
        changes: z.array(ColumnChangeSchema),
        destructive: z.array(ColumnChangeSchema),
        rejected: z.array(
          z.object({ fieldName: z.string(), reason: z.string() }),
        ),
      }),
    )
    .route({
      summary: "Preview extension table changes from publishing",
      description:
        "Shows the column changes publishing a schema's draft would make to its extension table, which are destructive and need name confirmation, and which are rejected.",
      method: "GET",
    }),

  /**
   * Link an existing `sql` schema to an extendable entity and provision its
   * table, from the schema's active version. The only way a schema becomes an
   * extension after creation — `schemaDefinitions.update` can't touch
   * `targetModel`.
   */
  register: oc
    .input(
      z.object({
        schemaDefinitionId: UUIDSchema,
        targetModel: z.string(),
      }),
    )
    .output(SchemaExtensionSchema)
    .route({
      summary: "Register a schema as an entity extension",
      description:
        "Links an existing SQL schema to an extendable entity and creates its extension table from the schema's active version. Returns the new extension.",
      method: "POST",
    }),

  /**
   * Unlink a schema: drops the extension table and everything in it, so
   * `confirmation` must equal the schema's name.
   */
  unregister: oc
    .input(
      z.object({
        schemaDefinitionId: UUIDSchema,
        confirmation: z.string(),
      }),
    )
    .output(z.object({ unregistered: z.boolean() }))
    .route({
      summary: "Unregister an entity extension",
      description:
        "Unlinks a schema from its entity and drops the extension table with all its data. Requires typing the schema name to confirm.",
      method: "POST",
    }),

  provision: oc
    .input(z.object({ schemaDefinitionId: UUIDSchema }))
    .output(SchemaExtensionSchema)
    .route({
      summary: "Provision an entity extension table",
      description:
        "Creates the physical extension table and indexes for a declared extension schema and registers it, all in one transaction.",
      method: "POST",
    }),

  sync: oc
    .input(z.object({ schemaDefinitionId: UUIDSchema }))
    .output(
      z.object({
        tableName: z.string(),
        changes: z.array(ColumnChangeSchema),
        createdIndexes: z.array(z.string()),
        droppedIndexes: z.array(z.string()),
      }),
    )
    .route({
      summary: "Sync an entity extension table",
      description:
        "Alters an extension table to match the schema's active version: renames, retypes, adds, and drops columns, then indexes. Refuses changes that would lose data.",
      method: "POST",
    }),

  /** Drops the table and its data. The confirm belongs in the calling UI. */
  deprovision: oc
    .input(z.object({ schemaDefinitionId: UUIDSchema }))
    .output(z.object({ deprovisioned: z.boolean() }))
    .route({
      summary: "Drop an entity extension table",
      description:
        "Drops the extension table and all its data and removes its registration. Destructive; the caller must confirm.",
      method: "POST",
    }),
} as const;

export type SchemaExtensionResponse = z.infer<typeof SchemaExtensionSchema>;
export type SchemaExtensionFieldExposureResponse = z.infer<
  typeof FieldExposureResponseSchema
>;
export type SchemaExtensionStatusResponse = z.infer<
  typeof StatusResponseSchema
>;
